import { useEffect, useMemo, useState } from "react";
import { fetchFamilyDashboard, fetchFamilyStudents } from "../../api/family";
import { fetchFamilyMemberCommunications } from "../../api/memberCommunication";
import { fetchFamilyNotifications } from "../../api/notification";

export type FamilyNavigationCounts = {
  home: number | null;
  schedule: number | null;
  communications: number | null;
  notifications: number | null;
};

export type FamilyNavigationSeedCounts = Partial<Record<keyof FamilyNavigationCounts, number>>;

type CachedCountsEntry = {
  counts: FamilyNavigationCounts;
  updatedAt: number;
};

const COUNTS_CACHE_TTL_MS = 30_000;
const EMPTY_COUNTS: FamilyNavigationCounts = {
  home: null,
  schedule: null,
  communications: null,
  notifications: null
};

const countsCache = new Map<string, CachedCountsEntry>();
const inflightLoads = new Map<string, Promise<FamilyNavigationCounts>>();

function normalizeSeedCounts(seedCounts?: FamilyNavigationSeedCounts): Partial<FamilyNavigationCounts> {
  if (!seedCounts) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(seedCounts).filter(([, value]) => typeof value === "number" && Number.isFinite(value))
  ) as Partial<FamilyNavigationCounts>;
}

function mergeCounts(
  current: FamilyNavigationCounts,
  next: Partial<FamilyNavigationCounts>
): FamilyNavigationCounts {
  return {
    home: next.home ?? current.home,
    schedule: next.schedule ?? current.schedule,
    communications: next.communications ?? current.communications,
    notifications: next.notifications ?? current.notifications
  };
}

function readCachedCounts(tenantSlug: string): FamilyNavigationCounts | null {
  const cached = countsCache.get(tenantSlug);

  if (!cached) {
    return null;
  }

  if (Date.now() - cached.updatedAt > COUNTS_CACHE_TTL_MS) {
    return cached.counts;
  }

  return cached.counts;
}

function cacheCounts(tenantSlug: string, counts: FamilyNavigationCounts) {
  countsCache.set(tenantSlug, { counts, updatedAt: Date.now() });
}

async function loadNavigationCounts(tenantSlug: string): Promise<FamilyNavigationCounts> {
  const existing = inflightLoads.get(tenantSlug);

  if (existing) {
    return existing;
  }

  const promise = (async () => {
    const [studentsResult, dashboardResult, communicationsResult, notificationsResult] = await Promise.allSettled([
      fetchFamilyStudents(tenantSlug),
      fetchFamilyDashboard(tenantSlug),
      fetchFamilyMemberCommunications(tenantSlug),
      fetchFamilyNotifications(tenantSlug)
    ]);

    const nextCounts: FamilyNavigationCounts = {
      home: studentsResult.status === "fulfilled" ? studentsResult.value.items.length : null,
      schedule: dashboardResult.status === "fulfilled" ? dashboardResult.value.upcomingItems.length : null,
      communications: communicationsResult.status === "fulfilled" ? communicationsResult.value.unreadCount : null,
      notifications:
        notificationsResult.status === "fulfilled"
          ? notificationsResult.value.items.filter((item) => !item.readAt).length
          : null
    };

    cacheCounts(tenantSlug, nextCounts);
    return nextCounts;
  })().finally(() => {
    inflightLoads.delete(tenantSlug);
  });

  inflightLoads.set(tenantSlug, promise);
  return promise;
}

export function useFamilyNavigationCounts(
  tenantSlug: string,
  seedCounts?: FamilyNavigationSeedCounts,
  enabled = true
): FamilyNavigationCounts {
  const normalizedSeedCounts = useMemo(() => normalizeSeedCounts(seedCounts), [seedCounts]);
  const [counts, setCounts] = useState<FamilyNavigationCounts>(() => {
    const cached = readCachedCounts(tenantSlug) ?? EMPTY_COUNTS;
    return mergeCounts(cached, normalizedSeedCounts);
  });

  useEffect(() => {
    if (!tenantSlug) {
      return;
    }

    if (Object.keys(normalizedSeedCounts).length === 0) {
      return;
    }

    const cached = readCachedCounts(tenantSlug) ?? EMPTY_COUNTS;
    const merged = mergeCounts(cached, normalizedSeedCounts);
    cacheCounts(tenantSlug, merged);
    setCounts(merged);
  }, [normalizedSeedCounts, tenantSlug]);

  useEffect(() => {
    if (!enabled || !tenantSlug) {
      return;
    }

    let cancelled = false;
    const cached = readCachedCounts(tenantSlug);

    if (cached) {
      setCounts((current) => mergeCounts(cached, current));
    }

    void loadNavigationCounts(tenantSlug).then((nextCounts) => {
      if (!cancelled) {
        setCounts((current) => mergeCounts(current, nextCounts));
      }
    }).catch(() => {
      if (!cancelled) {
        setCounts((current) => current);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, tenantSlug]);

  return counts;
}
